// src/store/builders/useProjectStatus.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const VALID_STATUSES = [
  "pending_approval",
  "pending_review",
  "rejected",
  "approved",
  "active",
  "checking",
  "hold",
  "completion_request",
  "completed",
];

const useProjectStatus = create(
  persist(
    (set, get) => ({
      projects: [],
      isLoading: false,
      error: null,
      selectedProject: null,
      documentStatus: null, 
      currentFilter: null, 

      fetchProjects: async (status = null, limit = 10, page = 1) => {
        set({ isLoading: true, error: null, currentFilter: status });
        try {
          const { token, user, userType } = useAuthStore.getState();

          if (!token) throw new Error("Authentication token missing!");

          const params = new URLSearchParams({ limit, page });
          
          if (userType === "builder") {
            if (!user || !user.id) throw new Error("Builder ID missing!"); // Check for user.id
            params.append("builderId", user.id); // Use user.id
          } else if (userType === "customer") {
            // Assuming for customer, the `user` object directly has `customerId`
            // If not, adjust `user.customerId` to the correct path within the customer's user object
            const customerId = user?.customerId; 
            if (!customerId) throw new Error("Customer ID missing!");
            params.append("customerId", customerId);
          } else {
            throw new Error("Invalid user type!");
          }
          if (status) params.append("status", status);

          const queryString = params.toString();

          const response = await axiosInstancev1.get(
            `/projects?${queryString}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );


          const fetchedProjects = response.data?.projects;
          if (!Array.isArray(fetchedProjects)) {
            console.error("API did not return a 'projects' array:", response.data);
            set({ projects: [], error: "Invalid data format from server." });
            toast.error("Received invalid data from server.");
          } else {
            set({ projects: fetchedProjects });
          }

        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message || "Failed to fetch projects";
          console.error("Fetch Projects Error:", errorMessage, error.response || error);
          set({ error: errorMessage, projects: [] });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },

      // fetchProjectById and other functions below are generally fine as they use projectId in path
      // and don't directly use builderId in the path (except for auth).

      fetchProjectById: async (projectId) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          const response = await axiosInstancev1.get(`/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          set({ selectedProject: response.data });
          return response.data;
        } catch (error) {
          console.error("Fetch Project Details Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to fetch project details";
          set({ error: errorMessage });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },

      checkCompletionDocumentsStatus: async (projectId) => {
        set({ isLoading: true, error: null, documentStatus: null }); 
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const response = await axiosInstancev1.get(
            `/projects/${projectId}/completion-documents-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            set({ documentStatus: response.data }); 
            return response.data; 
          } else {
            throw new Error(response.data.message || "Failed to check document status");
          }
        } catch (error) {
          console.error("Check Document Status Error:", error);
          const errorMessage = error.response?.data?.message || error.message || "Failed to check document status";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return null; 
        } finally {
          set({ isLoading: false });
        }
      },

      updateProjectStatus: async (projectId, status, commentOrReason = null) => { 
        set({ isLoading: true, error: null });
        try {
          if (!VALID_STATUSES.includes(status)) {
            throw new Error("Invalid status provided");
          }
          if ((status === "hold" || status === "rejected") && !commentOrReason?.trim()) {
            const requiredField = status === "hold" ? "Hold comment" : "Rejection reason";
            throw new Error(`${requiredField} is required when status is "${status}"`);
          }

          const { token } = useAuthStore.getState(); 
          if (!token) throw new Error("Authentication token missing!");

          const payload = { status };

          if (status === "hold") {
            payload.holdComment = commentOrReason; 
          } else if (status === "rejected") {
            payload.rejectionReason = commentOrReason;
          }

          const response = await axiosInstancev1.put(
            `/projects/${projectId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            toast.success("Project status updated successfully");
            await get().fetchProjects(get().currentFilter || null); 
            return { success: true };
          } else {
            throw new Error(response.data.message || "Failed to update status");
          }
        } catch (error) {
          console.error("Update Project Status Error:", error);
          const errorData = error.response?.data;
          const errorMessage = errorData?.message || error.message || "Failed to update project status";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return { success: false, message: errorMessage };
        } finally {
          set({ isLoading: false });
        }
      },

      uploadCompletionDocuments: async (projectId, documents) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const formData = new FormData();
          if (documents.specificationReport) formData.append("specificationReport", documents.specificationReport);
          if (documents.warrantyDocument) formData.append("warrantyDocument", documents.warrantyDocument);
          if (documents.completionCertificate) formData.append("completionCertificate", documents.completionCertificate);
          if (documents.siteImage) formData.append("siteImage", documents.siteImage);

          if (formData.entries().next().done) {
            toast.error("No documents selected for upload.");
            set({ isLoading: false });
            return { success: false, message: "No documents selected." };
          }

          const response = await axiosInstancev1.post(
            `/projects/${projectId}/completion-documents`, 
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data.success) {
            toast.success("Completion documents uploaded successfully!");
            return { success: true, updatedDocuments: response.data.documents }; 
          } else {
            throw new Error(response.data.message || "Failed to upload documents");
          }
        } catch (error) {
          console.error("Upload Completion Documents Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to upload completion documents";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return { success: false, message: errorMessage };
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "project-status-storage",
      getStorage: () => localStorage,
      partialize: (state) => {
        const { documentStatus, ...rest } = state;
        return rest;
      },
    }
  )
);

export default useProjectStatus;